import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FiAlertTriangle, FiChevronDown, FiCheck, FiRefreshCw } from 'react-icons/fi';
import { IoClose } from 'react-icons/io5';

const DEACTIVATE_REASONS = [
	{ value: 'Batch has expired', label: 'Batch has expired' },
	{ value: 'Batch is out of stock', label: 'Batch is out of stock' },
	{ value: 'Damaged or defective stock', label: 'Damaged or defective stock' },
	{ value: 'Discontinued product/batch', label: 'Discontinued product/batch' },
	{ value: 'Other', label: 'Other' },
];

const REACTIVATE_REASONS = [
	{ value: 'Batch has expired', label: 'Batch has expired' },
	{ value: 'Stock replenished / restocked', label: 'Stock replenished / restocked' },
	{ value: 'Batch is out of stock', label: 'Batch is out of stock' },
	{ value: 'Audit correction / inventory recount', label: 'Audit correction / inventory recount' },
	{ value: 'Other', label: 'Other' },
];

const getStatusClass = (label) => {
	const l = (label || '').toLowerCase();
	if (l.includes('expired')) return 'status-expired';
	if (l.includes('out of stock')) return 'status-out-of-stock';
	if (l.includes('disabled') || l.includes('inactive')) return 'status-disabled';
	return 'status-active';
};

const BatchConfirmModal = ({
	isOpen,
	onClose,
	onConfirm,
	actionType = 'deactivate',
	productName = '',
	batchNo = '',
	statusLabel = 'Expired',
	statusColor = '#DC2626',
	reason = '',
	onReasonChange,
	customReason = '',
	onCustomReasonChange,
	loader = false,
}) => {
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const dropdownRef = useRef(null);

	const isDeactivate = actionType === 'deactivate';
	const reasonOptions = isDeactivate ? DEACTIVATE_REASONS : REACTIVATE_REASONS;
	const isOther = reason === 'Other';
	const isOtherEmpty = isOther && !customReason?.trim();

	useEffect(() => {
		if (!isOpen) return;
		const handleKeyDown = (e) => {
			if (e.key === 'Escape' && !loader) {
				onClose();
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isOpen, loader, onClose]);

	useEffect(() => {
		if (!isDropdownOpen) return;
		const handleClickOutside = (e) => {
			if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
				setIsDropdownOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [isDropdownOpen]);

	useEffect(() => {
		if (isOpen) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = '';
		}
		return () => {
			document.body.style.overflow = '';
		};
	}, [isOpen]);

	if (!isOpen) return null;

	const handleOptionSelect = (optionValue) => {
		if (onReasonChange) {
			onReasonChange(optionValue);
		}
		setIsDropdownOpen(false);
	};

	const currentOptionLabel = reasonOptions.find((opt) => opt.value === reason)?.label || reason || 'Select a reason...';
	const statusBadgeClass = getStatusClass(statusLabel);

	return (
		<div
			className="modal show d-block batch-confirm-modal"
			tabIndex="-1"
			role="dialog"
			aria-modal="true"
			onClick={(e) => {
				if (e.target === e.currentTarget && !loader) {
					onClose();
				}
			}}
		>
			<div
				className="modal-dialog modal-dialog-top batch-confirm-dialog"
				onClick={(e) => e.stopPropagation()}
			>
				<div className="modal-content border-0 shadow-lg batch-confirm-content">
					{/* Modal Header */}
					<div className="modal-header border-0 batch-confirm-header">
						<div className="batch-confirm-header-left">
							<div className={`batch-confirm-icon-box ${isDeactivate ? 'deactivate' : 'reactivate'}`}>
								{isDeactivate ? <FiAlertTriangle size={20} /> : <FiRefreshCw size={19} />}
							</div>
							<div>
								<h5 className="batch-confirm-title">
									{isDeactivate ? 'Disable Batch?' : 'Reactivate Batch?'}
								</h5>
							</div>
						</div>
						<button
							type="button"
							className="batch-confirm-close-btn"
							onClick={onClose}
							disabled={loader}
							aria-label="Close"
							title="Close"
						>
							<IoClose size={20} color="#ffffff" />
						</button>
					</div>

					{/* Modal Body */}
					<div className="modal-body batch-confirm-body">
						<div className={`batch-confirm-banner mt-4 ${isDeactivate ? 'deactivate' : 'reactivate'}`}>
							{isDeactivate ? (
								<>
									You are about to disable this batch. This batch will no longer be available for new transaction or stock notifications.
									<span className="batch-confirm-banner-bold">Historical records will be retained.</span>
								</>
							) : (
								<>
									You are about to reactivate this batch. This batch will be available again for new transaction or stock notifications.
								</>
							)}
						</div>

						{/* 3-Column Info Card */}
						<div className="batch-confirm-info-card">
							<div className="row g-2 align-items-center">
								<div className="col-5">
									<div className="batch-confirm-info-label">Product Name</div>
									<div className="batch-confirm-info-value text-truncate" title={productName}>
										{productName || '—'}
									</div>
								</div>
								<div className="col-4">
									<div className="batch-confirm-info-label">Batch Number</div>
									<div className="batch-confirm-info-value text-truncate" title={batchNo}>
										{batchNo || '—'}
									</div>
								</div>
								<div className="col-3">
									<div className="batch-confirm-info-label">Current Status</div>
									<div className={`batch-confirm-info-value ${statusBadgeClass}`}>
										{statusLabel || (isDeactivate ? 'Expired' : 'Disabled')}
									</div>
								</div>
							</div>
						</div>

						<div className="batch-confirm-select-wrapper" ref={dropdownRef}>
							<label className="batch-confirm-label">
								Reason/status condition
							</label>

							<div
								role="button"
								tabIndex={0}
								onClick={() => setIsDropdownOpen((prev) => !prev)}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										setIsDropdownOpen((prev) => !prev);
									}
								}}
								className={`batch-confirm-select-trigger ${isDropdownOpen ? 'open' : ''}`}
							>
								<span className="text-truncate me-2">
									{currentOptionLabel}
								</span>
								<FiChevronDown
									size={16}
									className={`batch-confirm-trigger-icon ${isDropdownOpen ? 'rotated' : ''}`}
								/>
							</div>

							{isDropdownOpen && (
								<div className="batch-confirm-dropdown-menu">
									{reasonOptions.map((opt) => {
										const isSelected = reason === opt.value;
										return (
											<div
												key={opt.value}
												role="option"
												aria-selected={isSelected}
												onClick={() => handleOptionSelect(opt.value)}
												className={`batch-confirm-dropdown-item ${isSelected ? 'selected' : ''}`}
											>
												<span>{opt.label}</span>
												{isSelected && <FiCheck size={16} />}
											</div>
										);
									})}
								</div>
							)}
						</div>

						{isOther && (
							<div className="batch-confirm-custom-reason">
								<div className="d-flex align-items-center justify-content-between mb-1">
									<label className="batch-confirm-label m-0">
										<span>Specify Reason <span className="text-danger">*</span></span>
									</label>
									{customReason?.length > 0 && (
										<span className="batch-confirm-char-counter">
											{customReason.length}/150
										</span>
									)}
								</div>
								<input
									type="text"
									className={`batch-confirm-input ${isOtherEmpty ? 'has-error' : ''}`}
									placeholder={
										isDeactivate
											? 'e.g. Recalled by manufacturer or regulatory notice...'
											: 'e.g. Stock replenished from warehouse reserve...'
									}
									value={customReason}
									maxLength={150}
									onChange={(e) => onCustomReasonChange && onCustomReasonChange(e.target.value)}
									autoFocus
								/>
								{isOtherEmpty && (
									<div className="batch-confirm-error-text">
										Please enter a specific reason to proceed.
									</div>
								)}
							</div>
						)}
					</div>

					<div className="modal-footer border-0 batch-confirm-footer">
						<button
							type="button"
							className="batch-confirm-btn-cancel"
							onClick={onClose}
							disabled={loader}
						>
							Cancel
						</button>
						<button
							type="button"
							className={`batch-confirm-btn-submit ${isDeactivate ? 'deactivate' : 'reactivate'}`}
							onClick={() => {
								if (isOtherEmpty) return;
								onConfirm();
							}}
							disabled={loader || isOtherEmpty}
						>
							{loader ? (
								<>
									<span
										className="spinner-border spinner-border-sm me-2"
										role="status"
										aria-hidden="true"
									/>
									{isDeactivate ? 'Disabling...' : 'Reactivating...'}
								</>
							) : (
								isDeactivate ? 'Disable Batch' : 'Reactivate Batch'
							)}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

BatchConfirmModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onConfirm: PropTypes.func.isRequired,
	actionType: PropTypes.oneOf(['deactivate', 'reactivate']),
	productName: PropTypes.string,
	batchNo: PropTypes.string,
	statusLabel: PropTypes.string,
	statusColor: PropTypes.string,
	reason: PropTypes.string,
	onReasonChange: PropTypes.func,
	customReason: PropTypes.string,
	onCustomReasonChange: PropTypes.func,
	loader: PropTypes.bool,
};

export default BatchConfirmModal;
